import React, { PureComponent } from 'react'
import { createPortal } from 'react-dom'
import Icon from '@mdi/react'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import { mdiTune } from '@mdi/js'
import WWPresetPanel, {
  wwwcPresetStyleLeftOf,
  wwwcPresetStyleRightOf,
} from './WWPresetPanel'

// Presets WW/WC por modalidad DICOM
export const MODALITY_PRESETS = {
  'CT': [
    { name: 'Tejido Blando', ww: 400, wc: 50 },
    { name: 'Hueso', ww: 1500, wc: 300 },
    { name: 'Pulmón', ww: 1500, wc: -600 },
    { name: 'Cerebro', ww: 80, wc: 40 },
    { name: 'Abdomen', ww: 400, wc: 50 },
    { name: 'Columna', ww: 2000, wc: 400 }
  ],
  'MR': [
    { name: 'T1 Cerebro', ww: 400, wc: 200 },
    { name: 'T2 Cerebro', ww: 2000, wc: 1000 },
    { name: 'FLAIR', ww: 2000, wc: 1000 },
    { name: 'DWI', ww: 1000, wc: 500 },
    { name: 'T1 Columna', ww: 400, wc: 200 },
    { name: 'T2 Columna', ww: 2000, wc: 1000 }
  ],
  'CR': [
    { name: 'Tórax', ww: 2000, wc: 0 },
    { name: 'Abdomen', ww: 2000, wc: 0 },
    { name: 'Extremidades', ww: 2000, wc: 0 },
    { name: 'Columna', ww: 2000, wc: 0 }
  ],
  'DX': [
    { name: 'Tórax', ww: 2000, wc: 0 },
    { name: 'Abdomen', ww: 2000, wc: 0 },
    { name: 'Extremidades', ww: 2000, wc: 0 },
    { name: 'Columna', ww: 2000, wc: 0 }
  ],
  'US': [
    { name: 'General', ww: 255, wc: 127 },
    { name: 'Cardíaco', ww: 255, wc: 127 },
    { name: 'Vascular', ww: 255, wc: 127 }
  ],
  'NM': [
    { name: 'General', ww: 255, wc: 127 },
    { name: 'Cardíaco', ww: 255, wc: 127 },
    { name: 'Hueso', ww: 255, wc: 127 }
  ],
  'PT': [
    { name: 'General', ww: 255, wc: 127 },
    { name: 'Cerebro', ww: 255, wc: 127 },
    { name: 'Cuerpo', ww: 255, wc: 127 }
  ],
  'RF': [
    { name: 'General', ww: 255, wc: 127 },
    { name: 'Cardíaco', ww: 255, wc: 127 },
    { name: 'Vascular', ww: 255, wc: 127 }
  ],
  'XA': [
    { name: 'General', ww: 255, wc: 127 },
    { name: 'Cardíaco', ww: 255, wc: 127 },
    { name: 'Vascular', ww: 255, wc: 127 }
  ],
  'MG': [
    { name: 'General', ww: 255, wc: 127 },
    { name: 'Screening', ww: 255, wc: 127 }
  ]
}

// Presets por defecto para modalidades no específicas
export const DEFAULT_PRESETS = [
  { name: 'Tejido Blando', ww: 400, wc: 50 },
  { name: 'Hueso', ww: 1500, wc: 300 },
  { name: 'Pulmón', ww: 1500, wc: -600 },
  { name: 'Cerebro', ww: 80, wc: 40 }
]

export const RESET_WW_PRESET = { name: 'Reset', ww: null, wc: null }

/**
 * @param {string|null|undefined} modality
 * @returns {Array<{ name: string, ww: number|null, wc: number|null }>}
 */
export function getPresetsForModality(modality) {
  if (!modality) return DEFAULT_PRESETS.slice()
  return (MODALITY_PRESETS[modality] || DEFAULT_PRESETS).slice()
}

const iconSize = '1.2rem'
const iconColor = '#FFFFFF'
const activeColor = 'rgba(0, 255, 0, 1.0)'
const DISMISS_SUPPRESS_MS = 500

class WWPresetManager extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      anchorEl: null
    }
    this.suppressDismissUntil = 0
  }

  isControlled = () =>
    this.props.anchorEl !== undefined || this.props.open !== undefined

  componentDidUpdate(prevProps, prevState) {
    const wasOpen = this.wasOpen(prevProps, prevState)
    const isOpen = this.isPanelOpen()
    if (isOpen && !wasOpen) {
      this.suppressDismissUntil = Date.now() + DISMISS_SUPPRESS_MS
    }
  }

  wasOpen = (props = this.props, state = this.state) => {
    const controlled = props.anchorEl !== undefined || props.open !== undefined
    const anchorEl = controlled ? props.anchorEl || null : state.anchorEl
    return controlled
      ? props.open !== undefined
        ? !!props.open
        : Boolean(anchorEl)
      : Boolean(anchorEl)
  }

  isPanelOpen = () => this.wasOpen()

  handleClick = (event) => {
    this.suppressDismissUntil = Date.now() + DISMISS_SUPPRESS_MS
    this.setState({ anchorEl: event.currentTarget })
  }

  handleClose = () => {
    if (Date.now() < this.suppressDismissUntil) return
    if (this.props.onClose) {
      this.props.onClose()
    }
    if (!this.isControlled()) {
      this.setState({ anchorEl: null })
    }
  }

  handlePresetSelect = (preset) => {
    if (this.props.onPresetSelect) {
      this.props.onPresetSelect(preset)
    }
    this.handleClose()
  }

  getAnchorEl = () => {
    const controlled = this.isControlled()
    return controlled
      ? this.props.anchorEl || null
      : this.state.anchorEl
  }

  renderPanel = () => {
    const { currentModality, skin = 'classic', placement = 'left', zIndex = 160000 } =
      this.props
    const anchorEl = this.getAnchorEl()
    if (!anchorEl) return null

    const panelStyle =
      placement === 'right'
        ? wwwcPresetStyleRightOf(anchorEl)
        : wwwcPresetStyleLeftOf(anchorEl)

    return createPortal(
      <div
        className="udv-wwwc-presets-backdrop"
        style={{ zIndex }}
        role="presentation"
        onClick={this.handleClose}
        onContextMenu={(e) => {
          e.preventDefault()
          this.handleClose()
        }}
      >
        <WWPresetPanel
          modality={currentModality}
          skin={skin}
          style={panelStyle}
          onClick={(e) => e.stopPropagation()}
          onSelect={this.handlePresetSelect}
        />
      </div>,
      document.body
    )
  }

  render() {
    const { currentModality, hideTrigger } = this.props
    const controlled = this.isControlled()
    const anchorEl = this.getAnchorEl()
    const isOpen = controlled
      ? this.props.open !== undefined
        ? !!this.props.open
        : Boolean(anchorEl)
      : Boolean(anchorEl)

    return (
      <div style={{ display: 'contents' }}>
        {!hideTrigger && (
          <Tooltip title={`Presets WW/WC - ${currentModality || 'General'}`}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
                minWidth: 0,
              }}
            >
              <IconButton
                color="inherit"
                onClick={this.handleClick}
                aria-label="Presets WW/WC"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                style={{
                  padding: 8,
                  minWidth: 44,
                  minHeight: 44,
                  margin: 0,
                  backgroundColor: isOpen
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'transparent',
                }}
              >
                <Icon
                  path={mdiTune}
                  size={iconSize}
                  color={isOpen ? activeColor : iconColor}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {isOpen ? this.renderPanel() : null}
      </div>
    )
  }
}

export default WWPresetManager
